'use client';

import clsx from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import { type FormEvent, type ReactNode, type RefObject, useEffect, useRef } from 'react';
import { SearchBox, type SearchBoxProps } from 'react-instantsearch';

import Icon from '@commons/components/Icon';
import { LOCALES } from '@commons/i18n';
import { usePathname, useRouter } from '@commons/i18n/navigation';

import styles from './Input.module.scss';
import { getSearchURL } from './routing';

// Defining a debounce timer ID so it can be cleared.

let debounceTimerId: NodeJS.Timeout;

/**
 * Debounces the search query to avoid excessive API calls.
 */

const handleQuery = (query: string, refine: (value: string) => void) => {
  if (debounceTimerId) {
    clearTimeout(debounceTimerId);
  }

  debounceTimerId = setTimeout(() => refine(query), 500);
};

// Type definition of the SearchInput component properties.

type Props = SearchBoxProps & {
  searchIcon?: ReactNode;
  resetIcon?: ReactNode;
  hideResetIcon?: boolean;
};

/**
 * Algolia Search input component.
 *
 * Customizes the SearchBox component from Algolia's React InstantSearch library.
 *
 * @see https://www.algolia.com/doc/api-reference/widgets/search-box/react/
 */

export const AlgoliaSearchInput = ({
  searchIcon,
  resetIcon,
  hideResetIcon = false,
  ...props
}: Props) => {
  const t = useTranslations('search');

  return (
    <SearchBox
      queryHook={handleQuery}
      placeholder={t('input.placeholder')}
      translations={{ submitButtonTitle: t('label'), resetButtonTitle: t('input.clear') }}
      submitIconComponent={({ classNames }) => {
        const renderedIcon = searchIcon ? searchIcon : <Icon type="search" />;
        return <div className={classNames.submitIcon}>{renderedIcon}</div>;
      }}
      resetIconComponent={({ classNames }) => {
        const renderedIcon = resetIcon ? resetIcon : <Icon type="close" />;
        return <div className={classNames.resetIcon}>{renderedIcon}</div>;
      }}
      classNames={{
        root: styles.root,
        form: styles.form,
        input: styles.input,
        submit: styles.submit,
        reset: clsx(styles.reset, hideResetIcon && styles.hidden),
      }}
      {...props}
    />
  );
};

// Type definition of the Search Input properties.

type SearchProps = {
  onSearchSubmit?: (query: string) => void;
  inputReference?: RefObject<HTMLInputElement | null>;
};

/**
 * Search input component.
 */

export const SearchInput = ({ onSearchSubmit, inputReference }: SearchProps) => {
  const t = useTranslations('search');
  const locale = useLocale();
  const pathname = usePathname();
  const { push } = useRouter();

  const searchPagePath = t('path.global');
  const isSearchPage = pathname === searchPagePath;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const query = new FormData(event.currentTarget).get('query') as string;
    const searchURL = getSearchURL(searchPagePath, { filters: { '[query]': query } });

    if (onSearchSubmit) {
      onSearchSubmit(query);
    }

    if (isSearchPage && typeof window !== 'undefined') {
      const redirectURL = locale === LOCALES.FR ? `/${locale}${searchURL}` : searchURL;
      window.location.assign(redirectURL);
    } else {
      push(searchURL);
    }
  };

  // Use a ref to focus the input after the component mounts
  const defaultRef = useRef<HTMLInputElement>(null);
  const inputRef = inputReference || defaultRef;

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 100); // Delay to ensure the input is focused after the component mounts
    return () => clearTimeout(timer);
  }, [inputRef]);

  return (
    <div className={styles.root}>
      <form onSubmit={handleSubmit} className={styles.form}>
        <input
          type="text"
          name="query"
          aria-label={t('label')}
          ref={inputRef}
          className={styles.input}
          placeholder={t('input.placeholder')}
        />
        <button type="submit" aria-label={t('label')} className={styles.submit}>
          <div>
            <Icon type="search" />
          </div>
        </button>
      </form>
    </div>
  );
};

export default AlgoliaSearchInput;
